import { useState } from 'react';
import { CheckCircle, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Task } from '@/data/academyModules';

interface TaskSectionProps {
  task: Task;
  moduleId: number;
  onComplete: () => void;
  isCompleted: boolean;
}

const TaskSection = ({ task, moduleId, onComplete, isCompleted }: TaskSectionProps) => {
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});
  const [answeredQuiz, setAnsweredQuiz] = useState<Record<number, number>>({});

  const toggleChecklist = (index: number) => {
    setCheckedItems(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const answerQuiz = (questionIndex: number, optionIndex: number) => {
    if (answeredQuiz[questionIndex] !== undefined) return;
    setAnsweredQuiz(prev => ({ ...prev, [questionIndex]: optionIndex }));
  };

  return (
    <div className="border-2 border-orange-500/40 rounded-xl p-5 mt-6 bg-orange-500/3">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
          <PenLine className="w-4 h-4" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-foreground">Zadanie końcowe</h3>
          <p className="text-xs text-muted-foreground">Zastosuj wiedzę z tego modułu w praktyce</p>
        </div>
      </div>

      {task.type === 'text' && (
        <div className="space-y-4">
          {task.questions.map((q, i) => (
            <div key={i}>
              <label htmlFor={`task-${moduleId}-${i}`} className="block text-sm font-medium text-foreground mb-1.5">
                {q.label}
              </label>
              <textarea
                id={`task-${moduleId}-${i}`}
                placeholder={q.placeholder}
                rows={4}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg resize-vertical focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500 transition-colors placeholder:text-muted-foreground"
              />
            </div>
          ))}
        </div>
      )}

      {task.type === 'checklist' && (
        <ul className="space-y-2">
          {task.items.map((item, i) => (
            <li
              key={i}
              onClick={() => toggleChecklist(i)}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer select-none transition-all text-sm ${
                checkedItems[i]
                  ? 'bg-green-500/8 border-green-500/30 text-foreground'
                  : 'bg-background border-border hover:bg-muted/50'
              }`}
            >
              <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-[1.5px] transition-all ${
                checkedItems[i]
                  ? 'bg-green-500 border-green-500'
                  : 'border-border'
              }`}>
                {checkedItems[i] && <CheckCircle className="w-3.5 h-3.5 text-white" />}
              </div>
              <span className={checkedItems[i] ? 'line-through text-muted-foreground' : ''}>{item}</span>
            </li>
          ))}
        </ul>
      )}

      {task.type === 'quiz' && (
        <div className="space-y-6">
          {task.questions.map((q, qi) => {
            const chosen = answeredQuiz[qi];
            const answered = chosen !== undefined;
            return (
              <div key={qi}>
                <p className="text-sm font-semibold text-foreground mb-2">{qi + 1}. {q.q}</p>
                <div className="space-y-2">
                  {q.opts.map((opt, oi) => {
                    const isCorrect = oi === q.correct;
                    const isChosen = chosen === oi;
                    let cls = 'flex items-center gap-3 p-3 rounded-lg border text-sm transition-all ';
                    if (!answered) {
                      cls += 'bg-background border-border cursor-pointer hover:bg-muted/50';
                    } else if (isCorrect) {
                      cls += 'bg-green-500/8 border-green-500/30 text-green-700 dark:text-green-400';
                    } else if (isChosen) {
                      cls += 'bg-orange-500/8 border-orange-500/30 text-muted-foreground';
                    } else {
                      cls += 'bg-background border-border/50 text-muted-foreground opacity-60';
                    }
                    return (
                      <div
                        key={oi}
                        onClick={() => answerQuiz(qi, oi)}
                        className={cls}
                      >
                        <span className="w-6 h-6 rounded-full border-[1.5px] border-current flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {String.fromCharCode(65 + oi)}
                        </span>
                        {opt}
                      </div>
                    );
                  })}
                </div>
                {answered && (
                  <div className={`mt-2 text-xs p-3 rounded-lg ${
                    chosen === q.correct
                      ? 'bg-green-500/8 text-green-700 dark:text-green-400'
                      : 'bg-orange-500/8 text-orange-700 dark:text-orange-400'
                  }`}>
                    {q.feedback}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-5">
        {isCompleted ? (
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium">
            <CheckCircle className="w-4 h-4" />
            Moduł ukończony! Dobra robota.
          </div>
        ) : (
          <Button onClick={onComplete} className="bg-orange-500 hover:bg-orange-600 text-white">
            <CheckCircle className="w-4 h-4 mr-2" />
            Oznacz moduł jako ukończony
          </Button>
        )}
      </div>
    </div>
  );
};

export default TaskSection;
